import React, { useContext, useEffect, useRef, useState } from 'react'
import styled from 'styled-components'
import { Button, Container, Form, Icon, Modal } from 'components/base-ui'
import BtnBack from 'components/base-ui/BtnBack'
import { weight } from 'components/base-ui/Fonts'
import ScrollPage from 'components/base-ui/ScrollPage'
import InstructionsCombineCoupon from 'components/dine-in/InstructionsCombineCoupon'
import { Fonts } from 'components/hato-heart/base-ui'
import PromotionCouponCard, { DineInCoupon } from 'components/PromotionCouponCard'
import ScanQRCode from 'components/ScanQRCode'
import { AppContext } from 'contexts/AppProvider'
import { AdjustmentSourceEnumType, PromotionTypeEnum } from 'enums'
import {
  AdjustmentTargetEntityTypeEnum,
  AdjustmentTypeEnumType,
  ExternalCouponRewardTypeEnum,
} from 'graphQL/dinein/generate/operations'
import { cloneObject } from 'helpers'
import { sendAnalyticsPageView } from 'helpers/analytics'
import { generateSrcSet, getCouponThumbnailURL, getResizableURL, getSrcSetStr } from 'helpers/image'
import useScrollTopPage from 'hooks/useScrollTopPage'
import useSubPage from 'hooks/useSubPage'
import { useTranslation } from 'react-i18next'
import { CSSTransition, TransitionGroup } from 'react-transition-group'
import media from 'styles/media'
import {
  AdjustmentType,
  CartPromotionsType,
  ExternalCoupon,
  HandleApplyPromoCode,
  HandleRemoveCartAdjustment,
  HandleToShowPromotionDetails,
  OrderType,
} from './'

interface PromotionListProps {
  onApplyPromoCode: HandleApplyPromoCode
  onRemoveCartAdjustment: HandleRemoveCartAdjustment
  isLoadingPage: boolean
  isProcessing: boolean
  promotionIDApplying: number
  serialNumberApplying: string
  orderAndMemberCoupons: OrderType | undefined
  memberCouponsFP: DineInCoupon[]
  memberCouponsHH: DineInCoupon[]
  memberPromotions: CartPromotionsType
  generalPromotions: CartPromotionsType
  onClickCombineCoupon: VoidFunction
  onClickRefreshCoupon: VoidFunction
  onClickBack: VoidFunction
  allowedSharingCoupon: boolean
  handleToShowPromotionDetails: HandleToShowPromotionDetails
}
const iconHandleErrorNetWrok = `${process.env.PUBLIC_URL}/assets/food-passion/icon-modal-handle-error-network.png`
const iconCoupon = `${process.env.PUBLIC_URL}/assets/hato-heart/coupon/icon-coupon.png`

const PromotionList = ({
  onApplyPromoCode,
  onRemoveCartAdjustment,

  isLoadingPage,
  isProcessing,
  promotionIDApplying,
  serialNumberApplying,
  orderAndMemberCoupons,
  memberCouponsFP,
  memberCouponsHH,
  memberPromotions,
  generalPromotions,
  onClickCombineCoupon,
  onClickRefreshCoupon,
  onClickBack,
  allowedSharingCoupon,
  handleToShowPromotionDetails,
}: PromotionListProps) => {
  const { brand, isThaiLang, currentMembership, membershipProgram } = useContext(AppContext)
  const isBrandGonGang = brand?.slug === 'party-service' ? true : false
  const { t } = useTranslation()
  const [isShowScanQRCode, setIsShowScanQRCode] = useState<boolean>(false)
  const { subPageName, goToSubPage, goToSubPageWithReplaceHistory } = useSubPage({ onSubPageChange: () => {} })
  const [code, setCode] = useState<string>('')
  const [isShowModalPromoCodeError, setIsShowModalPromoCodeError] = useState<boolean>(false)
  const [promoCodeErrorMsg, setPromoCodeErrorMsg] = useState<string>('')
  const [
    isShowModalConfirmToRemoveCouponBeforeApplyNewCoupon,
    setIsShowModalConfirmToRemoveCouponBeforeApplyNewCoupon,
  ] = useState<boolean>(false)
  const [isLoadingRemoveAndAddNew, setIsLoadingRemoveAndAddNew] = useState<boolean>(false)
  const [promoCodeToConfirmApplyAfterRemove, setPromoCodeToConfirmApplyAfterRemove] = useState<string | undefined>('')
  const [externalCouponIDToConfirmApplyAfterRemove, setExternalCouponIDToConfirmApplyAfterRemove] = useState<
    string | undefined
  >('')
  const [memberCouponIDToConfirmApplyAfterRemove, setMemberCouponIDToConfirmApplyAfterRemove] = useState<
    number | undefined
  >()
  const cartAdjustments = orderAndMemberCoupons?.adjustments ?? []

  const cartAdjustmentsDiscount = cartAdjustments.filter(
    (adjustment) =>
      (adjustment.adjustmentType === AdjustmentTypeEnumType.Discount ||
        adjustment.adjustmentType === AdjustmentTypeEnumType.Concession) &&
      !(
        adjustment?.promotion?.targetEntity === AdjustmentTargetEntityTypeEnum.ShippingFee &&
        adjustment?.promotion?.type === PromotionTypeEnum.Auto
      )
  )

  sortingAdjustments(cartAdjustmentsDiscount)

  const memberCouponsDiscountFP = memberCouponsFP.filter(
    (coupon) => coupon.rewardType === AdjustmentTypeEnumType.Discount
  )
  sortingCoupons(memberCouponsDiscountFP)

  useEffect(() => {
    switch (subPageName) {
      case 'scan-qrcode':
        setIsShowScanQRCode(true)
        break
      default:
        setIsShowScanQRCode(false)
    }

    if (subPageName !== '') {
      sendAnalyticsPageView({
        pathname: window.location.pathname,
        search: window.location.search,
        brand: undefined,
      })
    }
  }, [subPageName])

  const scrollPageRef = useRef(null)

  useScrollTopPage({
    pageName: 'apply-coupon',
    scrollPageRef,
    skip: !isLoadingPage,
  })

  const handleApplyPromoCodeScan = (e) => {
    e.preventDefault()
    if (code === '') {
      setPromoCodeErrorMsg(t('promoCodeRequired'))
      setIsShowModalPromoCodeError(true)
      return false
    }
    if (isProcessing) {
      return false
    }
    onApplyPromoCode({
      promoCode: code,
      onCompleted: () => {
        setCode('')
        document.activeElement && (document.activeElement as HTMLElement).blur()
      },
      onFailed: () => {
        document.activeElement && (document.activeElement as HTMLElement).blur()
      },
    })
  }

  const enabledCombineCoupon = isBrandGonGang && currentMembership

  return (
    <>
      <PreloadImage
        alt="modal-food-passion-system-error"
        src={iconHandleErrorNetWrok}
        srcSet={generateSrcSet(iconHandleErrorNetWrok, 'png')}
      />
      <PreloadImage src={`${process.env.PUBLIC_URL}/assets/loading/loading-white.svg`} />

      <Modal
        isOpen={isShowModalPromoCodeError}
        onRequestClose={() => setIsShowModalPromoCodeError(false)}
        hideCloseButton={false}
      >
        <ModalContainer>
          <img
            alt="modal-food-passion-system-error"
            src={iconHandleErrorNetWrok}
            srcSet={generateSrcSet(iconHandleErrorNetWrok, 'png')}
          />
          <ModalTitle>{promoCodeErrorMsg}</ModalTitle>
        </ModalContainer>
      </Modal>

      <Modal
        isOpen={isShowModalConfirmToRemoveCouponBeforeApplyNewCoupon}
        onRequestClose={() => setIsShowModalConfirmToRemoveCouponBeforeApplyNewCoupon(false)}
        hideCloseButton={false}
      >
        <ModalContainer>
          <ModalTitle>{t('ModalConfirmToRemovePromotionBeforeTitle')}</ModalTitle>
          <ModalText style={{ marginBottom: '20px' }}>{t('ModalConfirmToRemovePromotionBeforeDes')}</ModalText>
          <Button
            onClick={() =>
              removeCouponAndApplyNewCouponToCart(
                promoCodeToConfirmApplyAfterRemove,
                externalCouponIDToConfirmApplyAfterRemove,
                memberCouponIDToConfirmApplyAfterRemove
              )
            }
            md
            minWidth="175px"
            enableFastClick={false}
            isProcessing={isLoadingRemoveAndAddNew}
            disabled={isLoadingRemoveAndAddNew}
          >
            {t('ModalConfirmToRemovePromotionBeforeBtn')}
          </Button>
        </ModalContainer>
      </Modal>

      <ScrollPage id="scroll-apply-promotion" ref={scrollPageRef} style={{ overflowX: 'hidden' }}>
        <ApplyCouponWrapper>
          <HeaderNav>
            <Container md>
              <BtnBack onClick={onClickBack} enableFastClick={false} />
            </Container>
          </HeaderNav>

          <PromotionCover />

          <ApplyCouponContainer>
            <form onSubmit={handleApplyPromoCodeScan} action="" data-testid="form-promotion">
              <SectionFieldCode>
                <InputWrapper>
                  <Form.PureInput
                    search
                    style={{
                      borderRadius: '30px',
                      border: 'none',
                      fontSize: '14px',
                      boxShadow: '2px 2px 10px rgba(0, 0, 0, 0.1)',
                    }}
                    placeholder={t('placeholderInputPromocode')}
                    value={code}
                    onChange={(e) => setCode(e.target.value)}
                    data-testid="input-promotion-code"
                    onBlur={() => {
                      if (code) {
                        onApplyPromoCode({
                          promoCode: code,
                          onCompleted: () => {
                            setCode('')
                          },
                        })
                      }
                    }}
                  />
                  <ButtonScanCode onClick={handleShowScanQRCode}>
                    <IconBarCode name="scan-barcode" />
                    {t('scanQRCodeIconText')}
                  </ButtonScanCode>
                </InputWrapper>
              </SectionFieldCode>
            </form>
          </ApplyCouponContainer>

          <ListContainers>
            {cartAdjustmentsDiscount.length > 0 ||
            memberCouponsDiscountFP.length > 0 ||
            memberCouponsHH.length > 0 ||
            memberPromotions.length > 0 ||
            generalPromotions.length > 0 ? (
              <SectionCouponInternal>
                {cartAdjustmentsDiscount.length > 0 && (
                  <>
                    <SectionCouponInternalTitle id="promotion-used-title">
                      {t('listPromotionTitleActived')}
                    </SectionCouponInternalTitle>
                    <ListPromotionWrapper>
                      <CouponList id="promotion-used-list">
                        <TransitionGroup>
                          {cartAdjustmentsDiscount.map((coupon, i) => {
                            let data = coupon.externalCoupon

                            if (
                              coupon.source === AdjustmentSourceEnumType.Promotion ||
                              coupon.source === AdjustmentSourceEnumType.MemberCoupon
                            ) {
                              const thumbnailImageURL = getCouponThumbnailURL({ coupon, width: '140', height: '140' })

                              data = {
                                id:
                                  coupon.source === AdjustmentSourceEnumType.Promotion
                                    ? coupon.promotionID
                                    : coupon.externalCoupon?.memberCouponRefID
                                    ? parseInt(coupon.externalCoupon.memberCouponRefID)
                                    : coupon.memberCouponID,
                                isAvailable: coupon.invalidReason ? false : true,
                                endTime: '',
                                amount: coupon.amountSatangs,
                                serialNumber: '',
                                rewardType: coupon.adjustmentType,
                                thumbnailImageURL,
                                name: isThaiLang ? coupon.labelTh : coupon.labelEn,
                                description: isThaiLang
                                  ? coupon.promotion.descriptionTh
                                  : coupon.promotion.descriptionEn,
                                source: coupon.source,
                                memberCouponRefID: '',
                                staffPrivilegeType: coupon.externalCoupon?.staffPrivilegeType,
                              }
                            }

                            return (
                              <CSSTransition key={`adjustment-discount-${i}`} timeout={500} classNames="coupon-fade">
                                <PromotionCouponCard
                                  data={data}
                                  isActive={true}
                                  externalCouponID={coupon.externalCouponID}
                                  promotionID={coupon.promotionID}
                                  hatoHeartMemberCouponID={
                                    coupon.externalCoupon?.memberCouponRefID
                                      ? parseInt(coupon.externalCoupon?.memberCouponRefID)
                                      : coupon.memberCouponID
                                  }
                                  handleApplyPromoCode={onApplyPromoCode}
                                  handleRemoveCartAdjustment={onRemoveCartAdjustment}
                                  isExternalMemberCoupon={false}
                                  invalidReason={coupon.invalidReason}
                                  handleToShowPromotionDetails={handleToShowPromotionDetails}
                                  targetEntity={coupon?.promotion?.targetEntity}
                                  haveMemberCondition={coupon?.promotion?.memberTierRankConditions.length > 0}
                                  isAutoPromotion={coupon?.promotion?.type === PromotionTypeEnum.Auto}
                                  isLoading={promotionIDApplying > 0 && promotionIDApplying === data.id && isProcessing}
                                />
                              </CSSTransition>
                            )
                          })}
                        </TransitionGroup>
                      </CouponList>
                    </ListPromotionWrapper>
                  </>
                )}

                {membershipProgram &&
                  (memberCouponsDiscountFP.length > 0 || memberCouponsHH.length > 0 || memberPromotions.length > 0) && (
                    <ListPromotionWrapper>
                      <Header>
                        <Fonts.H2 style={{ marginBottom: 0 }}>
                          <img
                            style={{ marginBottom: '3px', marginRight: '8px' }}
                            alt="manual-coupon"
                            src={`${process.env.PUBLIC_URL}/assets/generic/promotions/icon-section-member-promotions.png`}
                            srcSet={getSrcSetStr({
                              img: `${process.env.PUBLIC_URL}/assets/generic/promotions/icon-section-member-promotions.png`,
                              hires2x: true,
                              hires3x: true,
                            })}
                          />
                          {t('listPromotionTitleMember')}
                        </Fonts.H2>
                        {isBrandGonGang && (
                          <Title onClick={onClickRefreshCoupon}>
                            <Icon name="refresh-bold" size="20px" color={brand?.theme.colors.primary} />
                            <RefreshText>{t('dineIn.coupon.refresh')}</RefreshText>
                          </Title>
                        )}
                      </Header>
                      {enabledCombineCoupon && (
                        <InstructionsCombineCoupon onClickCombineCoupon={onClickCombineCoupon} />
                      )}
                      <CouponList>
                        {memberCouponsDiscountFP.map((coupon, i) => {
                          const _coupon = cloneObject(coupon)
                          _coupon.source = AdjustmentSourceEnumType.ExternalMemberCoupon

                          if (_coupon.membership?.id === currentMembership?.id && !allowedSharingCoupon) {
                            _coupon.membership = undefined
                          }

                          return (
                            <PromotionCouponCard
                              key={`coupon-discount-${i}`}
                              data={_coupon}
                              isActive={false}
                              isDisabled={!_coupon.isAvailable ? true : false}
                              handleApplyPromoCode={onApplyPromoCode}
                              handleRemoveCartAdjustment={onRemoveCartAdjustment}
                              isExternalMemberCoupon={true}
                              handleToShowPromotionDetails={handleToShowPromotionDetails}
                              haveMemberCondition={true}
                              isLoading={
                                serialNumberApplying !== '' &&
                                serialNumberApplying === _coupon.serialNumber &&
                                isProcessing
                              }
                            />
                          )
                        })}

                        {memberCouponsHH.map((_coupon, i) => {
                          return (
                            <PromotionCouponCard
                              key={i}
                              data={_coupon}
                              isActive={false}
                              isDisabled={!_coupon.isAvailable}
                              handleApplyPromoCode={onApplyPromoCode}
                              handleRemoveCartAdjustment={onRemoveCartAdjustment}
                              isExternalMemberCoupon={false}
                              handleToShowPromotionDetails={handleToShowPromotionDetails}
                              haveMemberCondition={true}
                              isLoading={promotionIDApplying > 0 && promotionIDApplying === _coupon.id && isProcessing}
                            />
                          )
                        })}

                        {memberPromotions.map((promotion, i) => {
                          const data: Omit<ExternalCoupon, 'staffPrivilegeType'> = {
                            id: promotion.id,
                            isAvailable: true,
                            endTime: '',
                            amount: 0,
                            serialNumber: promotion.code,
                            rewardType: ExternalCouponRewardTypeEnum.Discount,
                            thumbnailImageURL:
                              promotion.images && promotion.images.length > 0
                                ? getResizableURL(promotion.images, { width: '140', height: '140' })
                                : '',
                            name: isThaiLang ? promotion.nameTh : promotion.nameEn,
                            description: isThaiLang ? promotion.descriptionTh : promotion.descriptionEn,
                            source: AdjustmentSourceEnumType.Promotion,
                            memberCouponRefID: '',
                          }

                          return (
                            <PromotionCouponCard
                              key={`member-promotion-${i}`}
                              data={data}
                              isActive={false}
                              handleApplyPromoCode={onApplyPromoCode}
                              handleRemoveCartAdjustment={onRemoveCartAdjustment}
                              isExternalMemberCoupon={true}
                              handleToShowPromotionDetails={handleToShowPromotionDetails}
                              targetEntity={promotion.targetEntity}
                              haveMemberCondition={promotion.memberTierRankConditions.length > 0}
                              isLoading={promotionIDApplying > 0 && promotionIDApplying === data.id && isProcessing}
                            />
                          )
                        })}
                      </CouponList>
                    </ListPromotionWrapper>
                  )}

                {generalPromotions.length > 0 && (
                  <ListPromotionWrapper id="general-promotion-warpper">
                    <Fonts.H2>
                      <img
                        style={{ marginBottom: '5px', marginRight: '8px' }}
                        alt="manual-coupon"
                        src={`${process.env.PUBLIC_URL}/assets/generic/promotions/icon-section-general-promotions.png`}
                        srcSet={getSrcSetStr({
                          img: `${process.env.PUBLIC_URL}/assets/generic/promotions/icon-section-general-promotions.png`,
                          hires2x: true,
                          hires3x: true,
                        })}
                      />
                      {t('listPromotionTitleGeneric')}
                    </Fonts.H2>
                    <CouponList id="general-promotion-list">
                      {generalPromotions.map((promotion, i) => {
                        const data: DineInCoupon = {
                          id: promotion.id,
                          isAvailable: true,
                          endTime: '',
                          serialNumber: promotion.code,
                          rewardType: AdjustmentTypeEnumType.Discount,
                          thumbnailImageURL:
                            promotion.images && promotion.images.length > 0
                              ? getResizableURL(promotion.images, { width: '140', height: '140' })
                              : '',
                          name: isThaiLang ? promotion.nameTh : promotion.nameEn,
                          description: isThaiLang ? promotion.descriptionTh : promotion.descriptionEn,
                          source: AdjustmentSourceEnumType.Promotion,
                          memberCouponRefID: '',
                        }

                        return (
                          <PromotionCouponCard
                            key={`general-promotion-${i}`}
                            data={data}
                            isActive={false}
                            handleApplyPromoCode={onApplyPromoCode}
                            handleRemoveCartAdjustment={onRemoveCartAdjustment}
                            isExternalMemberCoupon={true}
                            handleToShowPromotionDetails={handleToShowPromotionDetails}
                            targetEntity={promotion.targetEntity}
                            haveMemberCondition={promotion.memberTierRankConditions.length > 0}
                            isLoading={promotionIDApplying > 0 && promotionIDApplying === data.id && isProcessing}
                          />
                        )
                      })}
                    </CouponList>
                  </ListPromotionWrapper>
                )}
              </SectionCouponInternal>
            ) : (
              <ManualCouponEmptyState>
                <img
                  style={{ marginBottom: '5px' }}
                  alt="manual-coupon"
                  src={iconCoupon}
                  srcSet={getSrcSetStr({
                    img: iconCoupon,
                    hires2x: true,
                    hires3x: true,
                  })}
                />
                <Fonts.BaseTitleBold>{t('emptyPromotionCoupon')}</Fonts.BaseTitleBold>
              </ManualCouponEmptyState>
            )}
          </ListContainers>
        </ApplyCouponWrapper>

        {cartAdjustmentsDiscount.length > 0 && (
          <ButtonWrapperConfirmCoupon>
            <Button md2 onClick={onClickBack} enableFastClick={false}>
              {t('buttonConfirmApplyCoupon')}
            </Button>
          </ButtonWrapperConfirmCoupon>
        )}
      </ScrollPage>

      <OverlayPage show={isShowScanQRCode}>
        <ScanQRCode
          title={t('scanQRCode')}
          show={isShowScanQRCode}
          onScanCompleted={(code) => {
            goToSubPageWithReplaceHistory('')
            onScanCompleted(code)
          }}
          onBack={() => goToSubPageWithReplaceHistory('')}
        />
      </OverlayPage>
    </>
  )

  function onScanCompleted(code: string) {
    setCode(code)
    onApplyPromoCode({
      promoCode: code,
      onCompleted: () => {
        setCode('')
      },
    })
  }

  function sortingCoupons(coupons: DineInCoupon[]) {
    return coupons.sort((a, b) => {
      return +new Date(a.endTime) - +new Date(b.endTime)
    })
  }

  function handleShowScanQRCode() {
    goToSubPage('scan-qrcode')
  }

  function sortingAdjustments(coupons: AdjustmentType[]) {
    return coupons.sort((a, b) => {
      if (a.externalCoupon && b.externalCoupon) {
        return +new Date(a.externalCoupon.endTime) - +new Date(b.externalCoupon.endTime)
      } else if (a.externalCoupon && !b.externalCoupon) {
        return 1
      } else {
        return 0
      }
    })
  }

  function removeCouponAndApplyNewCouponToCart(serialNumber, externalMemberCouponRefID, hatoHeartMemberCouponID) {
    setIsLoadingRemoveAndAddNew(true)
    const cartAdjustmentsDiscount = cartAdjustments.find(
      (adjustment) => adjustment.adjustmentType === AdjustmentTypeEnumType.Discount
    )

    let hhMemberCouponID: number | undefined = undefined
    if (cartAdjustmentsDiscount?.externalCoupon?.memberCouponRefID) {
      hhMemberCouponID = parseInt(cartAdjustmentsDiscount?.externalCoupon?.memberCouponRefID.toString())
    }

    onRemoveCartAdjustment({
      externalCouponID: cartAdjustmentsDiscount?.externalCouponID,
      promotionID:
        cartAdjustmentsDiscount?.externalCoupon.memberCouponRefID || cartAdjustmentsDiscount?.externalCouponID
          ? undefined
          : cartAdjustmentsDiscount?.promotionID,
      hatoHeartMemberCouponID: hhMemberCouponID,
      onCompleted: () => {
        onApplyPromoCode({
          externalMemberCouponRefID,
          promoCode: hatoHeartMemberCouponID || externalMemberCouponRefID ? undefined : serialNumber,
          hatoHeartMemberCouponID,
          onCompleted: () => {
            setIsLoadingRemoveAndAddNew(false)
            setIsShowModalConfirmToRemoveCouponBeforeApplyNewCoupon(false)
            setCode('')
            setPromoCodeToConfirmApplyAfterRemove('')
            setExternalCouponIDToConfirmApplyAfterRemove('')
            setMemberCouponIDToConfirmApplyAfterRemove(undefined)
          },
          onFailed: () => {
            setIsLoadingRemoveAndAddNew(false)
          },
        })
      },
      onFailed: () => {
        setIsLoadingRemoveAndAddNew(false)
      },
    })
  }
}

export default PromotionList

const PromotionCover = styled.div`
  height: 110px;
  background-color: white;
`

const ApplyCouponContainer = styled(Container)`
  position: relative;
  margin-top: -25px;
  z-index: 1;
`

const ApplyCouponWrapper = styled.div`
  display: flex;
  flex-direction: column;
  min-height: 100%;
`

const HeaderNav = styled.div`
  position: absolute;
  top: 17px;
  z-index: 1;

  ${Container} {
    display: flex;
    justify-content: space-between;
  }
`

const SectionFieldCode = styled.div`
  display: flex;
  padding-bottom: 25px;

  button {
    position: absolute;
    top: 50%;
    right: 6px;
    transform: translate(0, -50%);
  }
`

const InputWrapper = styled.div`
  position: relative;
  flex: 1;

  input {
    margin-bottom: 0;
    padding-right: 45px;

    @media (${media.screenXsMax}) {
      padding-right: 127px;
    }
  }
`

const ButtonScanCode = styled.div`
  display: flex;
  align-items: center;
  position: absolute;
  top: 50%;
  right: 20px;
  transform: translate(0, -50%);
  color: ${(p) => p.theme.colors.primary};
  padding: 10px;
`

const IconBarCode = styled(Icon)`
  margin-right: 5px;
`

const ManualCouponEmptyState = styled.div`
  text-align: center;
  padding-top: 50px;

  img {
    opacity: 0.5;
  }

  ${Fonts.BaseTitleBold} {
    margin-top: 5px;
    color: ${(p) => p.theme.colors.baseGrey};
  }
`

const ListPromotionWrapper = styled.div`
  display: flex;
  flex-direction: column;
  row-gap: 16px;
  margin-bottom: 20px;

  &:last-child {
    margin-bottom: 0;
  }
`

const CouponList = styled.div`
  padding-top: 15px;
`

const SectionCouponInternal = styled.div`
  margin-bottom: 30px;

  ${CouponList} {
    padding-top: 0;
  }
`

const SectionCouponInternalTitle = styled.div`
  font-weight: ${Fonts.weight.medium};
  font-size: 12px;
  color: ${(p) => p.theme.colors.lime900};
  margin-bottom: 8px;
`

const ButtonWrapperConfirmCoupon = styled.div`
  display: flex;
  justify-content: center;
  position: fixed;
  bottom: 30px;
  left: 0;
  right: 0;
  z-index: 100;

  .iphoneX & {
    padding-bottom: 50px;
  }
`

const OverlayPage = styled.div<{ show: boolean; fromLeft?: boolean }>`
  position: fixed;
  top: 0;
  right: 0;
  bottom: 0;
  left: 0;
  z-index: 100;
  background-color: white;
  transform: translate3d(100%, 0, 0);
  transition: 0.3s;
  pointer-events: none;
  visibility: hidden;

  ${(props) =>
    props.fromLeft
      ? `
    transform: translate3d(-100%, 0, 0);
    `
      : ''}

  ${(props) =>
    props.show
      ? `
    visibility: visible;
    pointer-events: auto;
    transform: translate3d(0, 0, 0);
    `
      : ''}
`

const ModalContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  flex-direction: column;
`

const ModalTitle = styled.div`
  font-weight: ${weight.medium};
  font-size: 20px;
  color: ${(props) => props.theme.colors.gray900};
  text-align: center;
  margin-top: 20px;
  white-space: pre-wrap;
  line-height: 1.4em;
`

const PreloadImage = styled.img`
  display: none;
`

const ModalText = styled.div`
  font-weight: ${weight.normal};
  font-size: 14px;
  color: ${(props) => props.theme.colors.gray700};
  text-align: center;
  margin-top: 10px;
  margin-bottom: 10px;
  white-space: pre-wrap;
`

const ListContainers = styled(Container)`
  background-color: #f7f7f7;
  padding-top: 24px;
  padding-bottom: 100px;
  flex: 1;

  .iphoneX & {
    padding-bottom: 120px;
  }
`

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
`

const Title = styled(Fonts.BaseTitleBold)`
  font-size: 14px;
  display: flex;
  align-items: center;
  column-gap: 4px;
`

const RefreshText = styled(Fonts.SmallTitleRegular)`
  color: ${({ theme }) => theme.colors.primary};
  white-space: nowrap;
  text-decoration: underline;
`
